import { keyframes } from "goober";
import { type CSSProperties, useId } from "react";
import { cursorBlink } from "../motion";

const ghost =
  "M2 16V10A8 8 0 0 1 18 10V16A2.6667 2 0 0 1 12.6667 16A2.6667 2 0 0 1 7.3333 16A2.6667 2 0 0 1 2 16Z";

const march = keyframes`
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -100; }
`;

const segments = [
  { color: "pink", start: 0, length: 50 },
  { color: "sky", start: 50, length: 25 },
  { color: "orange", start: 75, length: 12 },
  { color: "teal", start: 87, length: 13 },
];

const ringColors: Record<string, string> = {
  waiting: "var(--orange-600)",
  dragging: "var(--purple-600)",
  ready: "var(--green-600)",
  resting: "var(--gray-800)",
  error: "var(--red-600)",
};

const dashes = "4.1667 1.3889";

type Props = {
  state: string;
  size: number;
  showRing: boolean;
  planMode?: boolean;
  opaqueFill?: boolean;
};

export function GhostOutline({
  state,
  size,
  showRing,
  planMode,
  opaqueFill,
}: Props) {
  const id = useId();
  const ringSize = size + 4;
  const px = 20 / ringSize;
  const isActive =
    state === "thinking" || state === "loading" || state === "building";
  // The ring is a band 1px outside a body grown by 1px, so its gap stays even around the hem.
  const band = (style: CSSProperties, mask?: string, key?: string) => (
    <path
      key={key}
      d={ghost}
      pathLength={100}
      mask={mask}
      style={{
        fill: "none",
        strokeWidth: 4 * px,
        strokeLinejoin: "round",
        ...style,
      }}
    />
  );
  const chase = (period: number, mask?: string) =>
    segments.map((s) =>
      band(
        {
          stroke: `var(--${s.color}-600)`,
          strokeDasharray: `${s.length} ${100 - s.length}`,
          animation: `${march} ${period}s linear ${-(s.start / 100) * period}s infinite`,
        },
        mask,
        s.color
      )
    );
  const ring =
    planMode && isActive
      ? band({
          stroke: "var(--purple-600)",
          strokeDasharray: dashes,
          animation: `${march} 3000ms linear infinite`,
        })
      : state === "building"
        ? chase(5)
        : isActive
          ? chase(3, `url(#${id}dash)`)
          : band({
              stroke:
                planMode && state === "resting"
                  ? "var(--purple-600)"
                  : ringColors[state],
              ...(state === "resting" && {
                opacity: 0.5,
                strokeDasharray: "3 2",
                animation: `${march} 5000ms linear infinite`,
              }),
              ...((state === "waiting" ||
                state === "dragging" ||
                state === "error") && {
                animation: `${cursorBlink} 800ms ease-in-out infinite`,
              }),
            });
  return (
    <svg
      width={ringSize}
      height={ringSize}
      viewBox="0 0 20 20"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        translate: "-50% -50%",
        overflow: "visible",
      }}
    >
      <mask
        id={`${id}cut`}
        maskUnits="userSpaceOnUse"
        x="-2"
        y="-2"
        width="24"
        height="24"
      >
        <rect x="-2" y="-2" width="24" height="24" fill="white" />
        <path
          d={ghost}
          style={{
            fill: "black",
            stroke: "black",
            strokeWidth: 2 * px,
            strokeLinejoin: "round",
          }}
        />
      </mask>
      <mask
        id={`${id}dash`}
        maskUnits="userSpaceOnUse"
        x="-2"
        y="-2"
        width="24"
        height="24"
      >
        {band({
          stroke: "white",
          strokeDasharray: dashes,
          animation: `${march} 3000ms linear infinite`,
        })}
      </mask>
      {showRing && opaqueFill && (
        <path
          d={ghost}
          style={{
            fill: "var(--cell-bg, var(--surface-primary))",
            stroke: "var(--cell-bg, var(--surface-primary))",
            strokeWidth: 4 * px,
            strokeLinejoin: "round",
          }}
        />
      )}
      <path d={ghost} style={{ fill: "var(--control-bg)" }} />
      {showRing && <g mask={`url(#${id}cut)`}>{ring}</g>}
    </svg>
  );
}
